/**
 * Inner Gem - 17 Personas Data Configuration (English Version)
 * Contains metadata for all personas: Name, Energy, Image Path, Quote, Intention, Director's Note.
 * * Note: Image paths should be relative to the public folder or imported.
 */

export const PERSONAS_DATA = {
  // --- Group 1: Survival Crew ---
  '隐形人': {
    id: 'invisible',
    name: '隐形人', // Keep Chinese key for logic matching if needed
    englishName: 'The Invisible',
    energy: '20 · Shame',
    image: '/images/personas/THE INVISIBLE CARD -1.png', 
    imageZh: '/images/personas/1-隐形人.png',
    quote: "Please don't look at me... I am not enough.",
    intention: "Your bodyguard against judgment. To save you from the pain of rejection, he hides you in the shadows.",
    directorNote: "Gently hold his hand and say: 'Thank you for your protection, but I am safe now. We can stand in the light.'",
    group: 'survival'
  },
  '罪人': {
    id: 'sinner',
    name: '罪人',
    englishName: 'The Sinner',
    energy: '30 · Guilt',
    image: '/images/personas/THE SINNER CARD -2.png',
    imageZh: '/images/personas/2-罪人.png',
    quote: "It's all my fault... I must be punished.",
    intention: "He believes pain cancels out errors. He seeks redemption through self-punishment to feel 'good' again.",
    directorNote: "Tell him: 'Mistakes are lessons, not sentences. I have already forgiven myself.'",
    group: 'survival'
  },
  '旁观者': {
    id: 'bystander',
    name: '旁观者',
    englishName: 'The Bystander',
    energy: '50 · Apathy',
    image: '/images/personas/THE BYSTANDER CARD -3.png',
    imageZh: '/images/personas/3-旁观者.png',
    quote: "Whatever... It doesn't matter anyway.",
    intention: "Your emotional circuit breaker. To prevent the heartbreak of disappointment, he numbs all feelings.",
    directorNote: "Invite him to feel just one tiny joy. Say: 'I am not afraid to feel anymore. I want to be alive.'",
    group: 'survival'
  },
  '受害者': {
    id: 'victim',
    name: '受害者',
    englishName: 'The Victim',
    energy: '75 · Grief',
    image: '/images/personas/THE VICTIM CARD -4.png',
    imageZh: '/images/personas/4-受害者.png',
    quote: "Why me? This is so unfair.",
    intention: "He seeks connection and love through his wounds. Sadness is his way of honoring what was lost.",
    directorNote: "Hug him, but don't drown with him. Gently remind him: 'We are wounded, but we are still whole.'",
    group: 'survival'
  },
  '懦夫': {
    id: 'coward',
    name: '懦夫',
    englishName: 'The Coward',
    energy: '100 · Fear',
    image: '/images/personas/THE COWARD CARD -5.png',
    imageZh: '/images/personas/5-懦夫.png',
    quote: "What if I fail? The world is dangerous.",
    intention: "Your loyal risk assessor. He magnifies every danger just to ensure you survive another day.",
    directorNote: "Don't chase him away. Take him with you and say: 'I know you're scared. Let's just take this one small step together.'",
    group: 'survival'
  },
  '瘾君子': {
    id: 'addict',
    name: '瘾君子',
    englishName: 'The Addict',
    energy: '125 · Desire',
    image: '/images/personas/THE ADDICT CARD -6.png',
    imageZh: '/images/personas/6-瘾君子.png',
    quote: "I need more... Only that will make me happy.",
    intention: "He tries to fill an inner void with outer things. He mistakes the thrill of the chase for true happiness.",
    directorNote: "Pause and breathe. Tell him: 'The void cannot be filled from the outside. Come home, we are already enough.'",
    group: 'survival'
  },
  '暴君': {
    id: 'tyrant',
    name: '暴君',
    englishName: 'The Tyrant',
    energy: '150 · Anger',
    image: '/images/personas/THE TYRANT CARD -7.png',
    imageZh: '/images/personas/7-暴君.png',
    quote: "It's all your fault! Don't mess with me!",
    intention: "He uses attack as defense. Beneath that spiky armor hides a fragile child terrified of being hurt.",
    directorNote: "See his fragility. Say: 'Thank you for defending my boundaries. But now, we can speak with gentle power.'",
    group: 'survival'
  },
  '演员': {
    id: 'performer',
    name: '演员',
    englishName: 'The Performer',
    energy: '175 · Pride',
    image: '/images/personas/THE PERFORMER CARD -8.png',
    imageZh: '/images/personas/8-演员.png',
    quote: "Look at me! I don't need anyone.",
    intention: "He wears a mask of perfection to hide insecurity. He craves applause because he fears being unseen.",
    directorNote: "Take off the makeup. Tell him: 'No need to act. The real you is far more lovable than the perfect you.'",
    group: 'survival'
  },

  // --- Group 2: Creative Crew ---
  '探险家': {
    id: 'explorer',
    name: '探险家',
    englishName: 'The Explorer',
    energy: '200 · Courage',
    image: '/images/personas/THE EXPLORER CARD -9.png',
    imageZh: '/images/personas/9-探险家.png',
    quote: "I am willing to try.",
    intention: "The turning point of your script. He is not fearless; he feels the fear but chooses to step forward anyway.",
    directorNote: "Call him when you feel stuck. Make one tiny decision, and the energy will start to flow.",
    group: 'creative'
  },
  '实干家': {
    id: 'doer',
    name: '实干家',
    englishName: 'The Doer',
    energy: '250/310 · Willingness',
    image: '/images/personas/THE DOER CARD -10.png',
    imageZh: '/images/personas/10-实干家.png',
    quote: "What is the next step?",
    intention: "The builder of dreams. He doesn't complain or overthink; he focuses on solutions and grounded action.",
    directorNote: "Call him when you feel chaotic. Break the mountain into stones, and start building.",
    group: 'creative'
  },
  '智者': {
    id: 'sage',
    name: '智者',
    englishName: 'The Sage',
    energy: '350/400 · Reason',
    image: '/images/personas/THE SAGE CARD -11.png',
    imageZh: '/images/personas/11-智者.png',
    quote: "I see. Everything has a deeper meaning.",
    intention: "The detective of causality. He sees the gift behind every challenge and transforms pain into wisdom.",
    directorNote: "Call him when you are confused. Ask yourself: 'What is this situation trying to teach me?'",
    group: 'creative'
  },
  '疗愈者': {
    id: 'healer',
    name: '疗愈者',
    englishName: 'The Healer',
    energy: '500 · Love',
    image: '/images/personas/THE HEALER CARD -12.png',
    imageZh: '/images/personas/12-疗愈者.png',
    quote: "I love you, just because you exist.",
    intention: "Unconditional acceptance. He doesn't need to 'do' anything; his presence alone is warmth and cure.",
    directorNote: "Call him when you are in pain. Place a hand on your heart and say: 'It's okay. I am here with you.'",
    group: 'creative'
  },
  '庆祝者': {
    id: 'celebrator',
    name: '庆祝者',
    englishName: 'The Celebrator',
    energy: '540 · Joy',
    image: '/images/personas/THE CELEBRATOR CARD -13.png',
    imageZh: '/images/personas/13-庆祝者.png',
    quote: "This is a miracle!",
    intention: "The dancer of life. He needs no reason to be happy; he sees the divine spark in everything.",
    directorNote: "Call him when life feels heavy. Look up at the sky, smile, and realize life is a celebration.",
    group: 'creative'
  },
  '和平的观察者': {
    id: 'observer',
    name: '和平的观察者',
    englishName: 'The Peaceful Observer',
    energy: '600 · Peace',
    image: '/images/personas/THE PEACEFUL OBSERVER CARD -14.png',
    imageZh: '/images/personas/14-和平观察者.png',
    quote: "...",
    intention: "The unshakeable witness. He transcends duality, holding space for all clouds like the vast sky.",
    directorNote: "In any storm, retreat here. Breathe, and return to the eternal, silent center.",
    group: 'creative'
  },
  '开悟者': {
    id: 'enlightened',
    name: '开悟者',
    englishName: 'The Enlightened One',
    energy: '700+ · Enlightenment',
    image: '/images/personas/THE ENLIGHTENED ONE CARD -15.png',
    imageZh: '/images/personas/15-开悟者.png',
    quote: "I AM.",
    intention: "Pure light and consciousness. The ego dissolves into Oneness. This is your ultimate home.",
    directorNote: "You don't need to search for him; you are him. You simply forgot. Now, remember.",
    group: 'creative'
  }
};